import {baseApi} from '@/services/api/base-api';
import {mapCartError} from '@/services/api/utils/cart-error-mapper';

/**
 * RTK Query API for shopping cart operations
 */
export const cartApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetch a cart by its ID
     * @param {string} cartId - The ID of the cart to fetch
     * @returns {Promise<Cart>} - A promise that resolves to the cart data
     */
    getCart: builder.query({
      query: (cartId) => (cartId ? `/carts/${cartId}` : undefined),
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          return response.length > 0 ? response[0] : null;
        }
        return response;
      },
      transformErrorResponse: (response) => mapCartError(response),
      providesTags: (result, error, cartId) => [{type: 'Cart', id: cartId}],
    }),
    /**
     * Create a new cart
     * @param {Cart} cart - The cart data to create
     * @returns {Promise<Cart>} - A promise that resolves to the created cart data
     */
    createCart: builder.mutation({
      query: (cart) => ({
        url: '/carts',
        method: 'POST',
        body: cart,
      }),
      invalidatesTags: ['Cart'],
      transformErrorResponse: (response) => mapCartError(response),
    }),
    /**
     * Update an existing cart
     * @param {Cart} cart - The cart data to update
     * @returns {Promise<Cart>} - A promise that resolves to the updated cart data
     */
    updateCart: builder.mutation({
      query: (cart) => ({
        url: `/carts/${cart._id}`,
        method: 'PUT',
        body: cart,
      }),
      transformResponse: (response) => response ? response.cart : null,
      transformErrorResponse: (response) => mapCartError(response),
      invalidatesTags: (cart) => [{type: 'Cart', id: cart?._id}],
    }),
  }),
});

export const {useGetCartQuery, useCreateCartMutation, useUpdateCartMutation} = cartApi;
